import { useState, useEffect, useRef, useCallback } from 'react';


function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 280;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.fillStyle = '#a6a6a6';
    ctx.fillText('BrowserGuard 🔒', 2, 2);
    const ctx2 = canvas.getContext('2d');
    ctx2.font = 'italic 18px "Verdana"';
    ctx2.fillStyle = 'rgba(102, 125, 139, 0.6)';
    ctx2.fillText('Fingerprint', 2, 30);
    const dataURL = canvas.toDataURL();
    let hash = 0;
    for (let i = 0; i < dataURL.length; i++) {
      const char = dataURL.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  } catch {
    return null;
  }
}

function getAudioFingerprint() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    const audioCtx = new AudioContext();
    const oscillator = audioCtx.createOscillator();
    const analyser = audioCtx.createAnalyser();
    const gainNode = audioCtx.createGain();
    const scriptProcessor = audioCtx.createScriptProcessor(4096, 1, 1);
    gainNode.gain.value = 0.1;
    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.start(0);
    const buffer = new Float32Array(4096);
    scriptProcessor.onaudioprocess = (event) => {
      event.inputBuffer.copyFromChannel(buffer, 0);
    };
    setTimeout(() => {
      oscillator.stop();
      audioCtx.close();
    }, 50);
    let hash = 0;
    for (let i = 0; i < buffer.length; i += 100) {
      hash = ((hash << 5) - hash) + Math.round(buffer[i] * 10000);
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  } catch {
    return null;
  }
}

function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return null;
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return null;
    const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    return `${vendor}|${renderer}`;
  } catch {
    return null;
  }
}

function getFontList() {
  const baseFonts = ['monospace', 'sans-serif', 'serif'];
  const testFonts = [
    'Arial', 'Arial Black', 'Arial Narrow', 'Calibri', 'Cambria',
    'Comic Sans MS', 'Consolas', 'Courier New', 'Georgia', 'Helvetica',
    'Impact', 'Lucida Console', 'Lucida Sans', 'Microsoft YaHei',
    'Segoe UI', 'Tahoma', 'Times New Roman', 'Trebuchet MS',
    'Verdana', 'Wingdings', 'Roboto', 'Open Sans', 'Ubuntu',
  ];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.fontSize = testSize;
  span.style.lineHeight = 'normal';
  span.textContent = testString;
  const baseSizes = {};
  baseFonts.forEach((font) => {
    span.style.fontFamily = font;
    document.body.appendChild(span);
    baseSizes[font] = { width: span.offsetWidth, height: span.offsetHeight };
    document.body.removeChild(span);
  });
  const detected = [];
  testFonts.forEach((font) => {
    let detectedFont = null;
    baseFonts.forEach((baseFont) => {
      span.style.fontFamily = `"${font}", ${baseFont}`;
      document.body.appendChild(span);
      const width = span.offsetWidth;
      const height = span.offsetHeight;
      document.body.removeChild(span);
      if (
        width !== baseSizes[baseFont].width ||
        height !== baseSizes[baseFont].height
      ) {
        detectedFont = baseFont;
      }
    });
    if (detectedFont) detected.push(font);
  });
  return detected.join(',');
}

function getLanguageFingerprint() {
  return {
    language: navigator.language,
    languages: navigator.languages?.join(',') || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: navigator.platform || navigator.userAgentData?.platform || '',
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: navigator.deviceMemory || 0,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1,
  };
}

function getPerformanceFingerprint() {
  const perf = performance;
  if (!perf || !perf.memory) return null;
  return {
    jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
    totalJSHeapSize: perf.memory.totalJSHeapSize,
    usedJSHeapSize: perf.memory.usedJSHeapSize,
  };
}

function getMediaDevicesFingerprint() {
  try {
    return navigator.mediaDevices?.enumerateDevices?.().then((devices) => {
      return devices.map((d) => `${d.kind}:${d.groupId}`).join('|');
    }).catch(() => null);
  } catch {
    return null;
  }
}

function calculateScore(fingerprint) {
  let score = 5; // baseline — любой браузер с JS получает минимум 5
  let reasons = [];

  if (fingerprint.webgl) {
    score += 8;
    reasons.push('WebGL detected');
  }
  // НЕ штрафуем за отсутствие WebGL

  if (fingerprint.canvas) {
    score += 8;
    reasons.push('Canvas fingerprint');
  }
  // НЕ штрафуем

  if (fingerprint.audio) {
    score += 6;
    reasons.push('Audio fingerprint');
  }
  // НЕ штрафуем — createScriptProcessor deprecated в 2024

  if (fingerprint.fonts && fingerprint.fonts.split(',').length > 3) {
    score += 5;
    reasons.push(`Fonts: ${fingerprint.fonts.split(',').length}`);
  }
  // НЕ штрафуем

  // webdriver — единственный реальный red flag
  if (fingerprint.webdriver === false) {
    score += 10;
    reasons.push('webdriver: false');
  } else if (fingerprint.webdriver === true) {
    score -= 30;
    reasons.push('webdriver: TRUE — bot detected!');
  }

  if (fingerprint.hc >= 2) score += 2;
  if (fingerprint.dm >= 1) score += 2; // снизил с 4 до 1 — у многих 2-4 GB
  if (fingerprint.tz && fingerprint.tz !== '') score += 2;
  if (fingerprint.platform) score += 2;
  if (fingerprint.screenRes) score += 2;
  if (fingerprint.colorDepth === 24 || fingerprint.colorDepth === 30) score += 2;
  if (fingerprint.languages) score += 2;
  if (fingerprint.perfMemory) score += 3;
  if (fingerprint.mediaDevices !== null) score += 2;

  return { score, reasons };
}

// ============================================================
// BEHAVIORAL MODULE — проверяет поведение пользователя
// ============================================================
function useBehavioralCheck() {
  const [behaviorScore, setBehaviorScore] = useState(0);
  const [mouseMoves, setMouseMoves] = useState([]);
  const [hasScroll, setHasScroll] = useState(false);
  const [hasClick, setHasClick] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();

    const handleMouseMove = (e) => {
      setMouseMoves((prev) => {
        const newMoves = [...prev, { x: e.clientX, y: e.clientY, t: Date.now() }];
        return newMoves.slice(-50);
      });
    };

    const handleScroll = () => setHasScroll(true);
    const handleClick = () => setHasClick(true);
    const handleFocus = () => setHasFocus(true);

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleClick);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      let score = 0;
      const elapsed = Date.now() - startTimeRef.current;

      if (mouseMoves.length > 5) {
        let totalAngleChange = 0;
        for (let i = 2; i < mouseMoves.length; i++) {
          const dx1 = mouseMoves[i - 1].x - mouseMoves[i - 2].x;
          const dy1 = mouseMoves[i - 1].y - mouseMoves[i - 2].y;
          const dx2 = mouseMoves[i].x - mouseMoves[i - 1].x;
          const dy2 = mouseMoves[i].y - mouseMoves[i - 1].y;
          const angle1 = Math.atan2(dy1, dx1);
          const angle2 = Math.atan2(dy2, dx2);
          totalAngleChange += Math.abs(angle2 - angle1);
        }
        if (totalAngleChange > 0.5) score += 15;
        if (mouseMoves.length > 10) score += 10;
      }

      if (hasScroll) score += 10;
      if (hasClick) score += 10;
      if (hasFocus) score += 5;
      if (elapsed > 2000) score += 5;

      setBehaviorScore(score);
    }, 3000);

    return () => clearTimeout(timer);
  }, [mouseMoves, hasScroll, hasClick, hasFocus]);

  return { behaviorScore, mouseMoves };
}

// ============================================================
// CONSTANTS
// ============================================================
const PASS_THRESHOLD = 23;
const WARN_THRESHOLD = 0;
const MAX_SCORE = 100; // для нормализации progress bar

// ============================================================
// STYLES
// ============================================================
const styles = {
  checking: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0f0f0f',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #333',
    borderTop: '3px solid #6366f1',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0f0f0f',
    padding: 16,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: 16,
    padding: '36px 32px',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  },
  icon: {
    fontSize: 48,
    marginBottom: 16,
    display: 'block',
  },
  title: {
    color: '#f0f0f0',
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 12px',
  },
  subtitle: {
    color: '#888',
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  scoreBarWrapper: {
    background: '#2a2a2a',
    borderRadius: 999,
    height: 8,
    overflow: 'hidden',
    marginBottom: 8,
  },
  scoreBar: {
    height: '100%',
    borderRadius: 999,
    transition: 'width 0.6s ease, background 0.4s ease',
  },
  scoreLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#555',
    fontSize: 12,
    marginBottom: 24,
  },
  detailsToggle: {
    background: 'none',
    border: '1px solid #333',
    color: '#666',
    fontSize: 12,
    padding: '6px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 16,
    transition: 'border-color 0.2s, color 0.2s',
  },
  detailsList: {
    textAlign: 'left',
    background: '#111',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 20,
    maxHeight: 160,
    overflowY: 'auto',
  },
  detailItem: {
    color: '#6b6b8a',
    fontSize: 12,
    lineHeight: 1.8,
    borderBottom: '1px solid #1e1e1e',
    paddingBottom: 2,
    marginBottom: 2,
  },
  retryButton: {
    width: '100%',
    padding: '13px 0',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.1s',
    marginBottom: 10,
  },
  retryButtonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  attemptsNote: {
    color: '#444',
    fontSize: 12,
    marginTop: 4,
  },
};

if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  if (!document.head.querySelector('[data-browsergate-styles]')) {
    styleEl.setAttribute('data-browsergate-styles', '1');
    document.head.appendChild(styleEl);
  }
}

export default function BrowserGate({ children, onPass, onFail }) {
  const [status, setStatus] = useState('checking'); // checking | locked | warning | unlocked
  const [score, setScore] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [behaviorScore, setBehaviorScore] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(false);

  const { behaviorScore: bScore } = useBehavioralCheck();

  useEffect(() => {
    async function collectFingerprint() {
      const [
        canvas,
        audio,
        webgl,
        fonts,
        language,
        perfMemory,
        mediaDevices,
      ] = await Promise.all([
        getCanvasFingerprint(),
        getAudioFingerprint(),
        getWebGLFingerprint(),
        Promise.resolve(getFontList()),
        Promise.resolve(getLanguageFingerprint()),
        Promise.resolve(getPerformanceFingerprint()),
        getMediaDevicesFingerprint(),
      ]);

      const fp = {
        canvas,
        audio,
        webgl,
        fonts,
        webdriver: typeof navigator.webdriver === 'boolean' ? navigator.webdriver : null,
        timezone: language?.timezone || '',
        platform: language?.platform || '',
        hc: language?.hardwareConcurrency || 0,
        dm: language?.deviceMemory || 0,
        tz: language?.timezone || '',
        screenRes: language?.screenResolution || '',
        colorDepth: language?.colorDepth || 0,
        languages: language?.languages || '',
        perfMemory,
        mediaDevices: mediaDevices ?? null,
      };

      const { score: s, reasons: r } = calculateScore(fp);
      setScore(s);
      setReasons(r);
      setBehaviorScore(bScore);

      const total = s + bScore;

      if (total >= PASS_THRESHOLD) {
        setStatus('unlocked');
      } else if (total >= WARN_THRESHOLD) {
        setStatus('warning');
      } else {
        setStatus('locked');
      }
    }

    collectFingerprint();
  }, [bScore]);

  useEffect(() => {
    if (status === 'unlocked' && typeof onPass === 'function') onPass();
    if (status === 'locked' && typeof onFail === 'function') onFail();
  }, [status, onPass, onFail]);

  const handleRetry = useCallback(() => {
    if (cooldown) return;
    setCooldown(true);
    setAttempts((prev) => prev + 1);
    setTimeout(() => {
      window.location.reload();
    }, 800);
  }, [cooldown]);

  // --- Checking ---
  if (status === 'checking') {
    return (
      <div style={styles.checking}>
        <div style={styles.spinner} />
        <p style={{ color: '#888', marginTop: 12, fontFamily: 'sans-serif' }}>
          Проверяю браузер...
        </p>
      </div>
    );
  }

  // --- Unlocked: рендерим дочерний контент ---
  if (status === 'unlocked') {
    return <>{children}</>;
  }

  // --- Locked / Warning ---
  const totalScore = (score || 0) + behaviorScore;
  const isLocked = status === 'locked';

  // Нормализуем: 0–100 для progress bar
  // Считаем, что реальный максимум ≈ 80 баллов, чтобы бар смотрелся понятно
  const barPercent = Math.min(100, Math.max(0, (totalScore / 80) * 100));
  const barColor = isLocked
    ? '#ef4444'
    : totalScore >= PASS_THRESHOLD
    ? '#22c55e'
    : '#f59e0b';

  return (
    <div style={{ ...styles.overlay, animation: 'fadeIn 0.35s ease' }}>
      <div style={styles.card}>
        {/* Icon */}
        <span style={styles.icon}>{isLocked ? '🔒' : '⚠️'}</span>
        
        {/* Title */}
        <h2 style={styles.title}>
          {isLocked ? 'Access Restricted' : 'Please verify you are human'}
        </h2>
        
        {/* Subtitle */}
        <p style={styles.subtitle}>
          {isLocked
            ? 'We detected that your browser may be automated. Please refresh the page or use a standard browser.'
            : 'Our security system detected suspicious activity. Please confirm you are a real user.'}
        </p>
        
        {/* Score bar */}
        <div style={styles.scoreBarWrapper}>
          <div
            style={{
              ...styles.scoreBar,
              width: `${barPercent}%`,
              background: barColor,
            }}
          />
        </div>
        
        {/* Score label */}
        <div style={styles.scoreLabel}>
          <span>Trust Score: {totalScore} pts</span>
          <span>Threshold: {PASS_THRESHOLD} pts</span>
        </div>
        
        {/* Details toggle */}
        <button
          style={styles.detailsToggle}
          onClick={() => setShowDetails((v) => !v)}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#555';
            e.currentTarget.style.color = '#aaa';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#333';
            e.currentTarget.style.color = '#666';
          }}
        >
          {showDetails ? '▲ Hide Details' : '▼ Show Details'}
        </button>
        
        {/* Details list */}
        {showDetails && (
          <div style={styles.detailsList}>
            {reasons.length > 0 ? (
              reasons.map((r, i) => (
                <div key={i} style={styles.detailItem}>
                  {r.startsWith('webdriver: true') ? '🔴' : '🟢'} {r}
                </div>
              ))
            ) : (
              <div style={{ ...styles.detailItem, color: '#444' }}>
                Collecting data...
              </div>
            )}
            <div style={{ ...styles.detailItem, color: '#4b5563', marginTop: 6 }}>
              📊 Behavioral score: +{behaviorScore} pts
            </div>
          </div>
        )}
        
        {/* Retry button */}
        <button
          style={{
            ...styles.retryButton,
            ...(cooldown ? styles.retryButtonDisabled : {}),
          }}
          onClick={handleRetry}
          disabled={cooldown}
          onMouseEnter={(e) => {
            if (!cooldown) e.currentTarget.style.opacity = '0.85';
          }}
          onMouseLeave={(e) => {
            if (!cooldown) e.currentTarget.style.opacity = '1';
          }}
          onMouseDown={(e) => {
            if (!cooldown) e.currentTarget.style.transform = 'scale(0.98)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {cooldown ? 'Reloading...' : '🔄 Try Again'}
        </button>
        
        {/* Attempts note */}
        {attempts > 0 && (
          <p style={styles.attemptsNote}>
            Attempts: {attempts}{attempts >= 3 ? ' — try a different browser' : ''}
          </p>
        )}
      </div>
    </div>
  );
}